// -----------------------------------------------------------------------------
// Pure, testable helpers for the CMS "Customers" feature — see plans/CMS.md.
// Persistence/CRUD lives in src/components/api/companiesApi.ts; this file holds
// the domain logic (status predicates, overdue, homepage normalization, primary
// contact invariants, sorting, filtering, search).
// -----------------------------------------------------------------------------

import { todayLocal } from "@/components/api/companiesApi";
import type { Company, Contact, OfficeCount, Status } from "@/components/api/companiesApi";

export { todayLocal };

// --- Offices -----------------------------------------------------------------

/** Selectable office-count buckets, ascending. `4` is the open-ended "4+". */
export const OFFICE_COUNT_OPTIONS: OfficeCount[] = [1, 2, 3, 4];

/** Human label for an office-count bucket: 1/2/3/"4+", or "—" when unset. */
export function officeCountLabel(count: OfficeCount | null | undefined): string {
    if (count == null) return "—";
    return count >= 4 ? "4+" : String(count);
}

// --- Status ------------------------------------------------------------------

/** Full pipeline order (active first, terminal last). */
export const STATUS_ORDER: Status[] = [
    "New",
    "Contacted",
    "In process",
    "Attention needed",
    "Won",
    "Lost",
];

export const ACTIVE_STATUSES: Status[] = ["New", "Contacted", "In process", "Attention needed"];
export const TERMINAL_STATUSES: Status[] = ["Won", "Lost"];

export function isTerminal(status: Status): boolean {
    return status === "Won" || status === "Lost";
}

export function isActive(status: Status): boolean {
    return !isTerminal(status);
}

// --- Overdue / next-action ---------------------------------------------------

/**
 * Overdue ≡ nextAction != null && nextAction.dueDate < todayLocal() (strict, so
 * an action due today is NOT overdue). Terminal companies are never surfaced as
 * overdue regardless of their nextAction value.
 */
export function isOverdue(company: Company): boolean {
    if (isTerminal(company.status)) return false;
    if (!company.nextAction) return false;
    return company.nextAction.dueDate < todayLocal();
}

/** Active company with no open next-action ("needs a next action"). */
export function needsNextAction(company: Company): boolean {
    return isActive(company.status) && company.nextAction == null;
}

// --- Homepage ----------------------------------------------------------------

/**
 * Normalize a homepage entry: trim, and prepend "https://" when no scheme is
 * present, so quick entry like "acme.se" becomes a real URL. Empty input yields
 * "" (callers store null for the empty case).
 */
export function normalizeHomepage(input: string | null | undefined): string {
    const trimmed = (input ?? "").trim();
    if (!trimmed) return "";
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
}

// --- Primary contact invariants ----------------------------------------------
// Exactly one contact is primary whenever `contacts` is non-empty.

/**
 * Make the contact with `id` primary (radio behavior): un-stars every other
 * contact. Clicking the current primary is a no-op (the array is returned with
 * the same primary set).
 */
export function setPrimaryContact(contacts: Contact[], id: string): Contact[] {
    if (!contacts.some((c) => c.id === id)) return contacts;
    return contacts.map((c) => ({ ...c, isPrimary: c.id === id }));
}

/**
 * Append a contact, maintaining the invariant: the first contact added is
 * primary; any later contact is added non-primary (the existing primary stays).
 */
export function addContact(contacts: Contact[], contact: Contact): Contact[] {
    const isFirst = contacts.length === 0;
    return [...contacts, { ...contact, isPrimary: isFirst }];
}

/**
 * Remove the contact with `id`. If the removed contact was primary and others
 * remain, auto-promote the first remaining contact (by list order) to primary.
 */
export function removeContact(contacts: Contact[], id: string): Contact[] {
    const removed = contacts.find((c) => c.id === id);
    const remaining = contacts.filter((c) => c.id !== id);
    if (remaining.length === 0) return remaining;
    if (removed?.isPrimary && !remaining.some((c) => c.isPrimary)) {
        return remaining.map((c, i) => ({ ...c, isPrimary: i === 0 }));
    }
    return remaining;
}

export function primaryContact(contacts: Contact[]): Contact | null {
    return contacts.find((c) => c.isPrimary) ?? contacts[0] ?? null;
}

// --- Sorting -----------------------------------------------------------------

export type SortKey = "nextAction" | "lastContacted" | "offices" | "name";

function compareStrings(a: string, b: string): number {
    return a.localeCompare(b, undefined, { sensitivity: "base" });
}

/**
 * Default sort: next-action due date, overdue first, among non-terminal
 * companies. No-next-action companies sort last (by name). Terminal companies
 * are excluded from next-action surfacing — they sort after all active ones and
 * are ordered by name (their nextAction is ignored). Ties break by name A–Z.
 */
function compareByNextAction(a: Company, b: Company): number {
    const aTerminal = isTerminal(a.status) ? 1 : 0;
    const bTerminal = isTerminal(b.status) ? 1 : 0;
    if (aTerminal !== bTerminal) return aTerminal - bTerminal;

    // Terminal companies ignore nextAction entirely -> order by name.
    if (aTerminal === 1) return compareStrings(a.name, b.name);

    const aHas = a.nextAction ? 0 : 1;
    const bHas = b.nextAction ? 0 : 1;
    if (aHas !== bHas) return aHas - bHas;

    if (aHas === 0 && bHas === 0) {
        const byDue = compareStrings(a.nextAction!.dueDate, b.nextAction!.dueDate);
        if (byDue !== 0) return byDue;
    }
    return compareStrings(a.name, b.name);
}

/**
 * Last contacted, oldest first: never-contacted (null) sorts first (most stale),
 * then oldest→newest. Ties break by name A–Z.
 */
function compareByLastContacted(a: Company, b: Company): number {
    const aNever = a.lastContacted == null ? 0 : 1;
    const bNever = b.lastContacted == null ? 0 : 1;
    if (aNever !== bNever) return aNever - bNever;
    if (aNever === 1) {
        const byDate = compareStrings(a.lastContacted!, b.lastContacted!);
        if (byDate !== 0) return byDate;
    }
    return compareStrings(a.name, b.name);
}

/**
 * Number of offices, most first (4+ → 1). Unset (null) sorts last. Ties break
 * by name A–Z.
 */
function compareByOffices(a: Company, b: Company): number {
    const aUnset = a.officeCount == null ? 1 : 0;
    const bUnset = b.officeCount == null ? 1 : 0;
    if (aUnset !== bUnset) return aUnset - bUnset;
    if (aUnset === 0) {
        const byCount = b.officeCount! - a.officeCount!; // descending
        if (byCount !== 0) return byCount;
    }
    return compareStrings(a.name, b.name);
}

/** Returns a new, sorted array (stable). */
export function sortCompanies(companies: Company[], sort: SortKey): Company[] {
    const copy = [...companies];
    if (sort === "name") {
        copy.sort((a, b) => compareStrings(a.name, b.name));
    } else if (sort === "lastContacted") {
        copy.sort(compareByLastContacted);
    } else if (sort === "offices") {
        copy.sort(compareByOffices);
    } else {
        copy.sort(compareByNextAction);
    }
    return copy;
}

// --- Search ------------------------------------------------------------------

function onlyDigits(value: string): string {
    return value.replace(/\D/g, "");
}

/**
 * Case-insensitive substring across company name, contact names and contact
 * emails. Phone is matched digits-only on both sides, so "070 123" matches
 * "070-123 45 67".
 */
export function matchesSearch(company: Company, term: string): boolean {
    const q = term.trim().toLowerCase();
    if (!q) return true;

    if (company.name.toLowerCase().includes(q)) return true;

    const digits = onlyDigits(q);
    for (const c of company.contacts) {
        if (c.name && c.name.toLowerCase().includes(q)) return true;
        if (c.email && c.email.toLowerCase().includes(q)) return true;
        if (digits && c.phone) {
            const phoneDigits = onlyDigits(c.phone);
            if (phoneDigits && phoneDigits.includes(digits)) return true;
        }
    }
    return false;
}

// --- Filtering ---------------------------------------------------------------

export interface CompanyFilters {
    /** selected statuses (multi-select); bypassed when a search term is present */
    statuses: Status[];
    overdueOnly: boolean;
    needsAction: boolean;
    search: string;
}

/**
 * Apply the control-bar filters. When a search term is present the status
 * multi-select is bypassed (search runs across ALL statuses) so a Won/Lost
 * company can still be found. The overdue / needs-action quick toggles always
 * apply.
 */
export function filterCompanies(companies: Company[], filters: CompanyFilters): Company[] {
    const searchActive = filters.search.trim().length > 0;
    return companies.filter((c) => {
        if (!searchActive && !filters.statuses.includes(c.status)) return false;
        if (filters.overdueOnly && !isOverdue(c)) return false;
        if (filters.needsAction && !needsNextAction(c)) return false;
        if (searchActive && !matchesSearch(c, filters.search)) return false;
        return true;
    });
}
